import {
  collection,
  doc,
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  writeBatch,
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from './firebase';

const getDb = () => {
  if (!db) {
    throw new Error('Firebase Firestore is unavailable because Firebase is not configured.');
  }

  return db;
};

const REVIEWABLE_SUBMISSION_STATUSES = ['pending', 'submitted', 'in_review'];

const getTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value === 'string') return new Date(value).getTime() || 0;
  if (value instanceof Date) return value.getTime();
  return 0;
};

export const isReviewableSubmissionStatus = (status) => (
  REVIEWABLE_SUBMISSION_STATUSES.includes(status || 'pending')
);

const sortSubmissionsOldestFirst = (submissions) => (
  submissions.sort((a, b) => {
    const aTime = getTimestampMillis(a.createdAt || a.submittedAt || a.updatedAt);
    const bTime = getTimestampMillis(b.createdAt || b.submittedAt || b.updatedAt);
    return aTime - bTime;
  })
);

// ============ USER OPERATIONS ============

export const createUser = async (uid, userData) => {
  const userRef = doc(getDb(), 'users', uid);
  await setDoc(userRef, {
    ...userData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const getUser = async (uid) => {
  const userRef = doc(getDb(), 'users', uid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    return { id: userSnap.id, ...userSnap.data() };
  }
  return null;
};

export const updateUser = async (uid, updates) => {
  const userRef = doc(getDb(), 'users', uid);
  await updateDoc(userRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

export const getAllUsers = async () => {
  const usersRef = collection(getDb(), 'users');
  const snapshot = await getDocs(usersRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const updateUserRole = async (uid, role) => {
  const userRef = doc(getDb(), 'users', uid);
  await updateDoc(userRef, { 
    role,
    updatedAt: serverTimestamp()
  });
};

// ============ COURSE OPERATIONS ============

export const getCourses = async () => {
  const coursesRef = collection(getDb(), 'courses');
  const snapshot = await getDocs(coursesRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getCourse = async (courseId) => {
  const courseRef = doc(getDb(), 'courses', courseId);
  const courseSnap = await getDoc(courseRef);
  if (courseSnap.exists()) {
    return { id: courseSnap.id, ...courseSnap.data() };
  }
  return null;
};

// ============ MODULE OPERATIONS ============

export const getModules = async (courseId) => {
  const modulesRef = collection(getDb(), 'modules');
  const q = query(modulesRef, where('courseId', '==', courseId), orderBy('order'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getAllModules = async () => {
  const modulesRef = collection(getDb(), 'modules');
  const q = query(modulesRef, orderBy('order'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getModule = async (moduleId) => {
  const moduleRef = doc(getDb(), 'modules', moduleId);
  const moduleSnap = await getDoc(moduleRef);
  if (moduleSnap.exists()) {
    return { id: moduleSnap.id, ...moduleSnap.data() };
  }
  return null;
};

// ============ PROGRESS OPERATIONS ============

export const getUserProgress = async (uid) => {
  const progressRef = doc(getDb(), 'progress', uid);
  const progressSnap = await getDoc(progressRef);
  if (progressSnap.exists()) {
    return progressSnap.data();
  }
  // Return default progress if none exists
  return { completedModuleIds: [], unlockedModuleIds: [], careerLaunchEligible: false };
};

export const initializeUserProgress = async (uid, firstModuleId) => {
  const progressRef = doc(getDb(), 'progress', uid);
  await setDoc(progressRef, {
    userId: uid,
    completedModuleIds: [],
    unlockedModuleIds: [firstModuleId],
    careerLaunchEligible: false,
    updatedAt: serverTimestamp()
  });
};

export const updateUserProgress = async (uid, completedModuleIds, unlockedModuleIds, careerLaunchEligible = false) => {
  const progressRef = doc(getDb(), 'progress', uid);
  await setDoc(progressRef, {
    userId: uid,
    completedModuleIds,
    unlockedModuleIds,
    careerLaunchEligible,
    updatedAt: serverTimestamp()
  }, { merge: true });
};

// ============ SUBMISSION OPERATIONS ============

export const createSubmission = async (submissionData) => {
  const submissionsRef = collection(getDb(), 'submissions');
  const newSubmissionRef = doc(submissionsRef);
  await setDoc(newSubmissionRef, {
    ...submissionData,
    status: 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return newSubmissionRef.id;
};

export const getSubmission = async (submissionId) => {
  const submissionRef = doc(getDb(), 'submissions', submissionId);
  const submissionSnap = await getDoc(submissionRef);
  if (submissionSnap.exists()) {
    return { id: submissionSnap.id, ...submissionSnap.data() };
  }
  return null;
};

export const getUserSubmissions = async (uid) => {
  const submissionsRef = collection(getDb(), 'submissions');
  const q = query(submissionsRef, where('studentId', '==', uid), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getModuleSubmission = async (uid, moduleId) => {
  const submissionsRef = collection(getDb(), 'submissions');
  const q = query(
    submissionsRef, 
    where('studentId', '==', uid), 
    where('moduleId', '==', moduleId)
  );
  const snapshot = await getDocs(q);
  if (!snapshot.empty) {
    const submissions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return submissions.sort((a, b) => {
      const bTime = b.updatedAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
      const aTime = a.updatedAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    })[0];
  }
  return null;
};

export const resubmitSubmission = async (submissionId, updates) => {
  const submissionRef = doc(getDb(), 'submissions', submissionId);
  await updateDoc(submissionRef, {
    ...updates,
    status: 'pending',
    updatedAt: serverTimestamp()
  });
};

export const getPendingSubmissions = async () => {
  const submissionsRef = collection(getDb(), 'submissions');
  const snapshot = await getDocs(submissionsRef);
  const submissions = snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() }))
    .filter((submission) => isReviewableSubmissionStatus(submission.status));

  return sortSubmissionsOldestFirst(submissions);
};

export const getAllSubmissions = async () => {
  const submissionsRef = collection(getDb(), 'submissions');
  const q = query(submissionsRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const reviewSubmission = async (submissionId, status, feedback, reviewerId) => {
  const submissionRef = doc(getDb(), 'submissions', submissionId);
  await updateDoc(submissionRef, {
    status,
    feedback,
    reviewerId,
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const reviewSubmissionWithProgress = async (
  submissionId,
  status,
  feedback,
  reviewerId,
  studentId,
  completedModuleIds,
  unlockedModuleIds,
  careerLaunchEligible
) => {
  const database = getDb();
  const batch = writeBatch(database);
  const submissionRef = doc(database, 'submissions', submissionId);
  const progressRef = doc(database, 'progress', studentId);

  batch.update(submissionRef, {
    status,
    feedback,
    reviewerId,
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  batch.set(progressRef, {
    userId: studentId,
    completedModuleIds,
    unlockedModuleIds,
    careerLaunchEligible,
    updatedAt: serverTimestamp()
  }, { merge: true });

  await batch.commit();
};

// ============ REAL-TIME LISTENERS ============

export const subscribeToSubmissions = (callback) => {
  const submissionsRef = collection(getDb(), 'submissions');
  return onSnapshot(submissionsRef, (snapshot) => {
    const submissions = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter((submission) => isReviewableSubmissionStatus(submission.status));

    callback(sortSubmissionsOldestFirst(submissions));
  });
};

export const subscribeToUserProgress = (uid, callback) => {
  const progressRef = doc(getDb(), 'progress', uid);
  return onSnapshot(progressRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data());
    } else {
      callback({ completedModuleIds: [], unlockedModuleIds: [] });
    }
  });
};

// ============ CAREER LAUNCH OPERATIONS ============

export const getCareerLaunchProfile = async (uid) => {
  const profileRef = doc(getDb(), 'careerLaunch', uid);
  const profileSnap = await getDoc(profileRef);
  if (profileSnap.exists()) {
    return { id: profileSnap.id, ...profileSnap.data() };
  }
  return null;
};

export const saveCareerLaunchProfile = async (uid, profileData) => {
  const profileRef = doc(getDb(), 'careerLaunch', uid);
  await setDoc(profileRef, {
    ...profileData,
    studentId: uid,
    updatedAt: serverTimestamp()
  }, { merge: true });
};

export const getAllCareerLaunchProfiles = async () => {
  const profilesRef = collection(getDb(), 'careerLaunch');
  const snapshot = await getDocs(profilesRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const reviewCareerLaunchProfile = async (uid, reviewData) => {
  const profileRef = doc(getDb(), 'careerLaunch', uid);
  await updateDoc(profileRef, {
    ...reviewData,
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const publishTalentReport = async (slug, reportData) => {
  const reportRef = doc(getDb(), 'talentReports', slug);
  await setDoc(reportRef, {
    ...reportData,
    slug,
    publishedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }, { merge: true });
};

export const getTalentReport = async (slug) => {
  const reportRef = doc(getDb(), 'talentReports', slug);
  const reportSnap = await getDoc(reportRef);
  if (reportSnap.exists()) {
    return { id: reportSnap.id, ...reportSnap.data() };
  }
  return null;
};
